/**
 * WlCrmIntegrationForm — Formulário de configuração da integração CRM do WL.
 *
 * Espelha visualmente o componente `CrmIntegrationSection` da tela de
 * Tipo de Evento, porém:
 *   - Carrega entidades CRM do TENANT INTERNO via RPCs `wl_list_internal_crm_*`.
 *   - Não inclui o campo "Agendamentos simultâneos por horário" (exclusivo de
 *     agendamento online).
 *   - Salva via `wl_set_crm_integration`.
 */
import { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { Briefcase, Check, ChevronsUpDown, Layers, Loader2, Save, Tag, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { LEAD_SOURCES, LEAD_SOURCE_ICONS } from '@/lib/lead-sources';
import {
  useWlCrmIntegration,
  useWlInternalCrmDealTags,
  useWlInternalCrmQueues,
  useWlInternalCrmSellers,
  useWlInternalCrmWorkspaces,
  type WlCrmIntegrationPayload,
} from '@/hooks/wl/useWlCrmIntegration';

interface Props {
  /** Bloqueia edição (modo observador / sem permissão) */
  readOnly?: boolean;
}

export function WlCrmIntegrationForm({ readOnly = false }: Props) {
  const { data, isLoading, save, isSaving } = useWlCrmIntegration();
  const enabledQueries = !!data?.internal_tenant_configured;
  const { data: workspaces = [], isLoading: loadingWs } = useWlInternalCrmWorkspaces(enabledQueries);
  const { data: queues = [] } = useWlInternalCrmQueues(enabledQueries);
  const { data: sellers = [] } = useWlInternalCrmSellers(enabledQueries);
  const { data: dealTags = [] } = useWlInternalCrmDealTags(enabledQueries);

  // Estado local do formulário (espelha a config retornada pelo backend)
  const [form, setForm] = useState<WlCrmIntegrationPayload>({
    enabled: false,
    workspace_id: null,
    pipeline_id: null,
    phase_id: null,
    source: null,
    seller_assignment_mode: 'none',
    seller_id: null,
    distribution_queue_id: null,
    deal_tag_ids: [],
  });

  // Popovers
  const [workspaceOpen, setWorkspaceOpen] = useState(false);
  const [pipelineOpen, setPipelineOpen] = useState(false);
  const [phaseOpen, setPhaseOpen] = useState(false);
  const [tagsOpen, setTagsOpen] = useState(false);

  // Hidrata o form quando a query retorna
  useEffect(() => {
    if (!data) return;
    setForm({
      enabled: data.enabled,
      workspace_id: data.workspace_id,
      pipeline_id: data.pipeline_id,
      phase_id: data.phase_id,
      source: data.source,
      seller_assignment_mode: data.seller_assignment_mode,
      seller_id: data.seller_id,
      distribution_queue_id: data.distribution_queue_id,
      deal_tag_ids: data.deal_tag_ids || [],
    });
  }, [data]);

  // Derivados para popular selects encadeados
  const selectedWorkspace = useMemo(
    () => workspaces.find((w) => w.id === form.workspace_id),
    [workspaces, form.workspace_id],
  );
  const pipelines = selectedWorkspace?.pipelines || [];
  const selectedPipeline = pipelines.find((p) => p.id === form.pipeline_id);
  const phases = (selectedPipeline?.phases || []).filter((ph) => !ph.is_won && !ph.is_lost);
  const selectedPhase = phases.find((ph) => ph.id === form.phase_id);

  /** Atualiza campos do formulário */
  const patch = (updates: Partial<WlCrmIntegrationPayload>) =>
    setForm((prev) => ({ ...prev, ...updates }));

  /** Salva via RPC */
  const handleSave = async () => {
    await save(form);
  };

  if (isLoading || loadingWs) {
    return (
      <Card>
        <CardContent className="py-12 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-border/50">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Briefcase className="h-5 w-5 text-primary" />
          Integração CRM
        </CardTitle>
        <CardDescription>
          Crie automaticamente um negócio no CRM do tenant interno quando um novo tenant cliente for cadastrado.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Toggle principal */}
        <div className="flex items-center justify-between rounded-lg border border-border/50 p-4">
          <div>
            <p className="font-medium text-sm text-foreground">
              Criar Negócio CRM ao cadastrar novo tenant
            </p>
            <p className="text-xs text-muted-foreground">
              Aplica-se a qualquer novo tenant do tipo <strong>cliente</strong>. O negócio é criado no CRM do tenant interno do WL.
            </p>
          </div>
          <Switch
            checked={form.enabled}
            disabled={readOnly}
            onCheckedChange={(v) => patch({ enabled: v })}
          />
        </div>

        {form.enabled && (
          <div className="space-y-4 pl-1 border-l-2 border-primary/20 ml-2">
            <div className="pl-4 space-y-4">
              {/* Linha 1: Workspace + Pipeline */}
              <div className="grid gap-4 sm:grid-cols-2">
                {/* Workspace */}
                <div className="space-y-2">
                  <Label>Workspace <span className="text-destructive">*</span></Label>
                  <Popover open={workspaceOpen} onOpenChange={setWorkspaceOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        disabled={readOnly}
                        className="w-full justify-between font-normal"
                      >
                        <div className="flex items-center gap-2 truncate">
                          <Layers className="h-4 w-4 text-muted-foreground shrink-0" />
                          <span className={cn('truncate', !selectedWorkspace && 'text-muted-foreground')}>
                            {selectedWorkspace?.name || 'Selecione o workspace'}
                          </span>
                        </div>
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[300px] p-0" align="start">
                      <Command>
                        <CommandList>
                          <CommandEmpty>Nenhum workspace encontrado.</CommandEmpty>
                          <CommandGroup>
                            {workspaces.map((ws) => (
                              <CommandItem
                                key={ws.id}
                                value={ws.name}
                                onSelect={() => {
                                  if (ws.id !== form.workspace_id) {
                                    patch({ workspace_id: ws.id, pipeline_id: null, phase_id: null });
                                  }
                                  setWorkspaceOpen(false);
                                }}
                              >
                                <Layers className="h-4 w-4 mr-2 text-muted-foreground" />
                                <span className="flex-1 truncate">{ws.name}</span>
                                <Check
                                  className={cn(
                                    'ml-2 h-4 w-4',
                                    form.workspace_id === ws.id ? 'opacity-100' : 'opacity-0',
                                  )}
                                />
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Pipeline */}
                <div className="space-y-2">
                  <Label>Pipeline <span className="text-destructive">*</span></Label>
                  {form.workspace_id ? (
                    <Popover open={pipelineOpen} onOpenChange={setPipelineOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          disabled={readOnly}
                          className="w-full justify-between font-normal"
                        >
                          <div className="flex items-center gap-2 truncate">
                            <Layers className="h-4 w-4 text-muted-foreground shrink-0" />
                            <span className={cn('truncate', !selectedPipeline && 'text-muted-foreground')}>
                              {selectedPipeline?.name || 'Selecione o pipeline'}
                            </span>
                          </div>
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[300px] p-0" align="start">
                        <Command>
                          <CommandList>
                            <CommandEmpty>Nenhum pipeline encontrado.</CommandEmpty>
                            <CommandGroup>
                              {pipelines.map((pip) => (
                                <CommandItem
                                  key={pip.id}
                                  value={pip.name}
                                  onSelect={() => {
                                    if (pip.id !== form.pipeline_id) {
                                      patch({ pipeline_id: pip.id, phase_id: null });
                                    }
                                    setPipelineOpen(false);
                                  }}
                                >
                                  <Layers className="h-4 w-4 mr-2 text-muted-foreground" />
                                  <span className="flex-1 truncate">{pip.name}</span>
                                  <Check
                                    className={cn(
                                      'ml-2 h-4 w-4',
                                      form.pipeline_id === pip.id ? 'opacity-100' : 'opacity-0',
                                    )}
                                  />
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  ) : (
                    <Button variant="outline" disabled className="w-full justify-between font-normal text-muted-foreground">
                      Selecione o workspace primeiro
                    </Button>
                  )}
                </div>
              </div>

              {/* Linha 2: Fase + Origem */}
              <div className="grid gap-4 sm:grid-cols-2">
                {/* Fase */}
                <div className="space-y-2">
                  <Label>Fase <span className="text-destructive">*</span></Label>
                  {form.pipeline_id ? (
                    <>
                      <Popover open={phaseOpen} onOpenChange={setPhaseOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            role="combobox"
                            disabled={readOnly}
                            className="w-full justify-between font-normal"
                          >
                            <div className="flex items-center gap-2 truncate">
                              {selectedPhase?.color && (
                                <span
                                  className="w-2.5 h-2.5 rounded-full inline-block shrink-0"
                                  style={{ backgroundColor: selectedPhase.color }}
                                />
                              )}
                              <span className={cn('truncate', !selectedPhase && 'text-muted-foreground')}>
                                {selectedPhase
                                  ? `${selectedPhase.name}${selectedPhase.probability_percent != null ? ` (${selectedPhase.probability_percent}%)` : ''}`
                                  : 'Selecione a fase'}
                              </span>
                            </div>
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[300px] p-0" align="start">
                          <Command>
                            <CommandList>
                              <CommandEmpty>Nenhuma fase disponível.</CommandEmpty>
                              <CommandGroup>
                                {phases.map((ph) => (
                                  <CommandItem
                                    key={ph.id}
                                    value={ph.name}
                                    onSelect={() => {
                                      patch({ phase_id: ph.id });
                                      setPhaseOpen(false);
                                    }}
                                  >
                                    {ph.color && (
                                      <span
                                        className="w-2.5 h-2.5 rounded-full inline-block mr-2 shrink-0"
                                        style={{ backgroundColor: ph.color }}
                                      />
                                    )}
                                    <span className="flex-1 truncate">
                                      {ph.name}
                                      {ph.probability_percent != null && (
                                        <span className="text-muted-foreground"> ({ph.probability_percent}%)</span>
                                      )}
                                    </span>
                                    <Check
                                      className={cn(
                                        'ml-2 h-4 w-4',
                                        form.phase_id === ph.id ? 'opacity-100' : 'opacity-0',
                                      )}
                                    />
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                      <p className="text-xs text-muted-foreground">
                        Fases de ganho/perda são excluídas automaticamente
                      </p>
                    </>
                  ) : (
                    <Button variant="outline" disabled className="w-full justify-between font-normal text-muted-foreground">
                      Selecione o pipeline primeiro
                    </Button>
                  )}
                </div>

                {/* Origem */}
                <div className="space-y-2">
                  <Label>Origem <span className="text-destructive">*</span></Label>
                  <Select
                    value={form.source || ''}
                    disabled={readOnly}
                    onValueChange={(v) => patch({ source: v || null })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione a origem" />
                    </SelectTrigger>
                    <SelectContent>
                      {LEAD_SOURCES.map((s) => {
                        const Icon = LEAD_SOURCE_ICONS[s.id];
                        return (
                          <SelectItem key={s.id} value={s.id}>
                            <div className="flex items-center gap-2">
                              {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
                              {s.label}
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Linha 3: Atribuição vendedor + Vendedor/Fila */}
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Atribuição do vendedor</Label>
                  <Select
                    value={form.seller_assignment_mode}
                    disabled={readOnly}
                    onValueChange={(v) =>
                      patch({
                        seller_assignment_mode: v as WlCrmIntegrationPayload['seller_assignment_mode'],
                        seller_id: v === 'manual' ? form.seller_id : null,
                        distribution_queue_id: v === 'distribution_queue' ? form.distribution_queue_id : null,
                      })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Nenhum</SelectItem>
                      <SelectItem value="manual">Manual (selecionar vendedor)</SelectItem>
                      <SelectItem value="distribution_queue">Fila de distribuição</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {form.seller_assignment_mode === 'manual' && (
                  <div className="space-y-2">
                    <Label>Vendedor <span className="text-destructive">*</span></Label>
                    <Select
                      value={form.seller_id || ''}
                      disabled={readOnly}
                      onValueChange={(v) => patch({ seller_id: v || null })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione o vendedor" />
                      </SelectTrigger>
                      <SelectContent>
                        {sellers.map((s) => (
                          <SelectItem key={s.user_id} value={s.user_id}>
                            {s.full_name || s.email || s.user_id.slice(0, 8).toUpperCase()}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {form.seller_assignment_mode === 'distribution_queue' && (
                  <div className="space-y-2">
                    <Label>Fila de distribuição <span className="text-destructive">*</span></Label>
                    <Select
                      value={form.distribution_queue_id || ''}
                      disabled={readOnly}
                      onValueChange={(v) => patch({ distribution_queue_id: v || null })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione a fila" />
                      </SelectTrigger>
                      <SelectContent>
                        {queues.map((q) => (
                          <SelectItem key={q.id} value={q.id}>
                            {q.name} ({q.distribution_method})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              {/* Linha 4: Tags do negócio (opcional, multi-select) */}
              <div className="space-y-2">
                <Label>Tags do negócio</Label>
                <Popover open={tagsOpen} onOpenChange={setTagsOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      disabled={readOnly}
                      className="w-full justify-between font-normal h-auto min-h-10 py-2"
                    >
                      <div className="flex items-center gap-1.5 flex-wrap text-left">
                        <Tag className="h-4 w-4 text-muted-foreground shrink-0" />
                        {form.deal_tag_ids.length === 0 ? (
                          <span className="text-muted-foreground">Selecione as tags (opcional)</span>
                        ) : (
                          dealTags
                            .filter((t) => form.deal_tag_ids.includes(t.id))
                            .map((t) => (
                              <Badge
                                key={t.id}
                                variant="secondary"
                                className="gap-1"
                                style={t.color ? { backgroundColor: `${t.color}20`, color: t.color, borderColor: `${t.color}40` } : undefined}
                              >
                                {t.name}
                                {!readOnly && (
                                  <X
                                    className="h-3 w-3 cursor-pointer opacity-70 hover:opacity-100"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      patch({ deal_tag_ids: form.deal_tag_ids.filter((id) => id !== t.id) });
                                    }}
                                  />
                                )}
                              </Badge>
                            ))
                        )}
                      </div>
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[320px] p-0" align="start">
                    <Command>
                      <CommandList>
                        <CommandEmpty>Nenhuma tag cadastrada no CRM interno.</CommandEmpty>
                        <CommandGroup>
                          {dealTags.map((t) => {
                            const checked = form.deal_tag_ids.includes(t.id);
                            return (
                              <CommandItem
                                key={t.id}
                                value={t.name}
                                onSelect={() => {
                                  patch({
                                    deal_tag_ids: checked
                                      ? form.deal_tag_ids.filter((id) => id !== t.id)
                                      : [...form.deal_tag_ids, t.id],
                                  });
                                }}
                              >
                                {t.color && (
                                  <span
                                    className="w-2.5 h-2.5 rounded-full inline-block mr-2 shrink-0"
                                    style={{ backgroundColor: t.color }}
                                  />
                                )}
                                <span className="flex-1 truncate">{t.name}</span>
                                <Check className={cn('ml-2 h-4 w-4', checked ? 'opacity-100' : 'opacity-0')} />
                              </CommandItem>
                            );
                          })}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                <p className="text-xs text-muted-foreground">
                  As tags selecionadas serão aplicadas automaticamente a cada negócio criado.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Rodapé com botão Salvar */}
        {!readOnly && (
          <div className="flex justify-end pt-2 border-t border-border/30">
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              Salvar configuração
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
